export const CATEGORIES = [
  "Psicologia",
  "Neuropsicologia",
  "Infância",
  "Adolescência",
  "Vida Adulta",
  "TDAH",
  "TEA",
  "Ansiedade",
  "Aprendizagem",
];

export const CATEGORY_GUIDE = [
  {
    category: "Psicologia",
    text: "A Psicologia é a ciência e a prática dedicadas a compreender emoções, comportamentos e processos mentais. Na clínica, ela oferece um espaço de escuta e cuidado para compreender demandas e construir caminhos de bem-estar.",
  },
  {
    category: "Neuropsicologia",
    text: "A neuropsicologia é a área da Psicologia que estuda a relação entre o cérebro e o comportamento: atenção, memória, linguagem, raciocínio, funções executivas e emoções. É ela que fundamenta os processos de avaliação neuropsicológica.",
  },
  {
    category: "Infância",
    text: "A infância é a fase das grandes bases: desenvolvimento emocional, cognitivo, social e da aprendizagem. Olhar com atenção para essa etapa ajuda a compreender necessidades, dificuldades e potencialidades desde cedo.",
  },
  {
    category: "Adolescência",
    text: "A adolescência é um período de intensas transformações — no corpo, no cérebro, nas relações e na construção da identidade. É uma fase em que o apoio psicológico pode fazer diferença na organização das emoções e das escolhas.",
  },
  {
    category: "Vida Adulta",
    text: "A vida adulta reúne demandas de trabalho, rotina, relações e responsabilidades. Questões emocionais, de atenção e de organização também podem aparecer — ou acompanhar a pessoa desde a infância — e merecem cuidado.",
  },
  {
    category: "TDAH",
    text: "O TDAH (Transtorno de Déficit de Atenção com Hiperatividade) é uma condição do neurodesenvolvimento que envolve dificuldades de atenção, organização e, em alguns casos, inquietação e impulsividade. Pode estar presente na infância e seguir na vida adulta.",
  },
  {
    category: "TEA",
    text: "O TEA (Transtorno do Espectro Autista) é uma condição do neurodesenvolvimento que envolve particularidades na comunicação, na interação social e em padrões de interesse e comportamento — em diferentes graus e formas de manifestação.",
  },
  {
    category: "Ansiedade",
    text: "A ansiedade é uma resposta natural do corpo diante de ameaças e desafios. Quando se torna frequente, intensa ou passa a limitar a rotina, pode ser um sinal de que merece atenção e cuidado profissional.",
  },
  {
    category: "Aprendizagem",
    text: "Aprender envolve atenção, memória, linguagem, raciocínio e emoções. Dificuldades nesse processo podem ter origens diversas — e compreendê-las é o primeiro passo para construir caminhos efetivos na escola e na vida.",
  },
];

export const ARTICLES = [
  {
    slug: "o-que-e-neuropsicologia",
    title: "O que é neuropsicologia?",
    category: "Neuropsicologia",
    minutes: 4,
    date: "2026-07-02",
    excerpt: "A neuropsicologia estuda a relação entre o cérebro e o comportamento. Entenda o que essa área da Psicologia investiga e como ela se aproxima das pessoas na prática clínica.",
    sections: [
      {
        paragraphs: [
          "A neuropsicologia é a área da Psicologia que se dedica a compreender como o cérebro se relaciona com o comportamento, as emoções e as capacidades cognitivas — como atenção, memória, linguagem, raciocínio e funções executivas (o conjunto de habilidades que usamos para planejar, organizar e concluir tarefas).",
          "Na prática, isso significa olhar para a pessoa de forma integrada: não apenas para uma dificuldade isolada, mas para o funcionamento como um todo, considerando história de vida, contexto e momento atual.",
        ],
      },
      {
        heading: "Onde a neuropsicologia atua",
        paragraphs: [
          "O campo mais conhecido é a avaliação neuropsicológica: um processo estruturado, com etapas definidas, que investiga o funcionamento cognitivo, emocional e comportamental. Ela pode ser considerada quando há dúvidas sobre atenção, memória, aprendizagem, desenvolvimento ou hipóteses como TDAH e TEA.",
          "A neuropsicologia também contribui para o planejamento de acompanhamentos e orientações, sempre a partir da compreensão individual de cada pessoa — criança, adolescente ou adulto.",
        ],
      },
      {
        heading: "O que a neuropsicologia não é",
        paragraphs: [
          "Não é um teste rápido que 'entrega um diagnóstico', nem uma promessa de resultado. É um processo técnico e criterioso, conduzido por psicólogo com formação na área, seguindo as normas do Conselho Federal de Psicologia.",
          "Se você tem uma inquietação sobre atenção, memória, aprendizagem ou desenvolvimento, uma conversa inicial já ajuda a compreender se esse é o caminho adequado.",
        ],
      },
    ],
  },
  {
    slug: "o-que-e-tea",
    title: "TEA: o que é o Transtorno do Espectro Autista?",
    category: "TEA",
    minutes: 5,
    date: "2026-06-18",
    excerpt: "O TEA é uma condição do neurodesenvolvimento que envolve comunicação, interação social e padrões de comportamento. Entenda o que se sabe, como se manifesta e o papel da avaliação.",
    sections: [
      {
        paragraphs: [
          "O Transtorno do Espectro Autista (TEA) é uma condição do neurodesenvolvimento caracterizada por particularidades em duas grandes áreas: a comunicação e a interação social, e os padrões de interesse e comportamento — como movimentos repetitivos, interesses intensos e específicos, e maior ou menor sensibilidade a estímulos sensoriais.",
          "A palavra 'espectro' é importante: o TEA se manifesta de formas e graus muito diferentes de pessoa para pessoa. Não existe um único 'jeito' de ser autista — e é por isso que processos de compreensão individual são tão relevantes.",
        ],
      },
      {
        heading: "Sinais que costumam levar famílias a buscar informação",
        paragraphs: [
          "Na infância, famílias frequentemente relatam dúvidas sobre o contato visual, a resposta ao nome, a forma de brincar, a comunicação, a interação com outras crianças ou reações intensas a sons, texturas e mudanças de rotina. Em adolescentes e adultos, a busca pode nascer de uma trajetória de sensação de 'funcionar diferente', dificuldades sociais persistentes ou interesses muito intensos.",
          "É importante dizer: nenhum sinal isolado confirma ou descarta nada. A compreensão responsável exige um processo de avaliação criterioso, que considera desenvolvimento, história e contexto.",
        ],
      },
      {
        heading: "O papel da avaliação neuropsicológica",
        paragraphs: [
          "Quando há dúvidas relacionadas ao TEA, a avaliação neuropsicológica contribui para compreender o funcionamento da pessoa de forma ampla — aspectos cognitivos, emocionais, comportamentais e do desenvolvimento — e orientar os próximos passos junto à família e, quando pertinente, à escola e a outros profissionais.",
          "Mais do que um rótulo, o objetivo é compreender necessidades e potencialidades, para que cada pessoa receba o suporte adequado à sua singularidade.",
        ],
      },
    ],
  },
  {
    slug: "ansiedade-quando-a-preocupacao-pede-atencao",
    title: "Ansiedade: quando a preocupação pede atenção?",
    category: "Ansiedade",
    minutes: 4,
    date: "2026-06-05",
    excerpt: "A ansiedade faz parte da vida — mas, quando se torna constante ou limitante, merece cuidado. Entenda a diferença entre a ansiedade natural e aquela que pede atenção.",
    sections: [
      {
        paragraphs: [
          "A ansiedade é uma resposta natural do organismo: ela nos prepara para desafios e nos protege diante de riscos. Sentir ansiedade antes de uma prova, de uma conversa importante ou de uma mudança faz parte da experiência humana.",
          "A atenção é necessária quando a preocupação se torna frequente, intensa, difícil de controlar ou começa a limitar a rotina — no sono, no trabalho, nos estudos, nas relações ou na sensação de descanso.",
        ],
      },
      {
        heading: "Sinais de que pode ser hora de buscar apoio",
        paragraphs: [
          "Preocupação excessiva e persistente, sensação de tensão constante, dificuldade para relaxar, irritabilidade, sintomas físicos (como coração acelerado, aperto no peito ou tensão muscular) e evitação de situações do dia a dia são sinais que merecem ser ouvidos — sem alarme, mas também sem menosprezo.",
          "Em crianças e adolescentes, a ansiedade pode aparecer de formas diferentes: queixas físicas frequentes, medo intenso de errar, recusa escolar ou grande necessidade de reasseguramento.",
        ],
      },
      {
        heading: "Como a psicoterapia pode contribuir",
        paragraphs: [
          "A psicoterapia oferece um espaço para compreender o que sustenta a ansiedade: pensamentos, contextos, história de vida e formas de enfrentamento. A partir dessa compreensão, novos caminhos podem ser construídos — no ritmo de cada pessoa.",
          "Em alguns casos, a ansiedade se relaciona com outras questões, como atenção e sono. Quando há essa pertinência, uma avaliação mais ampla pode ajudar a diferenciar e compreender o quadro com responsabilidade.",
        ],
      },
    ],
  },
  {
    slug: "adolescencia-transformacoes-e-saude-emocional",
    title: "Adolescência: o que acontece nessa fase e quando buscar apoio?",
    category: "Adolescência",
    minutes: 4,
    date: "2026-05-28",
    excerpt: "A adolescência é um período de intensas transformações no cérebro, nas emoções e nas relações. Entenda essa fase e os sinais de que um apoio pode ajudar.",
    sections: [
      {
        paragraphs: [
          "A adolescência é uma das fases de maior transformação da vida: o cérebro segue em desenvolvimento (especialmente as regiões ligadas ao planejamento e ao controle de impulsos), o corpo muda, as relações ganham novos contornos e a identidade está em plena construção.",
          "É natural que essa fase venha acompanhada de intensidade emocional, questionamentos e, às vezes, conflitos — em casa, na escola e consigo mesmo(a).",
        ],
      },
      {
        heading: "Quando um apoio pode fazer diferença",
        paragraphs: [
          "Tristeza persistente, isolamento, ansiedade intensa, queda brusca no desempenho escolar, mudanças marcantes de sono ou apetite, conflitos frequentes e dificuldades de atenção ou organização que impactam a rotina são sinais de que o adolescente pode se beneficiar de um espaço de escuta e cuidado.",
          "A psicoterapia com adolescentes respeita o momento de desenvolvimento: é um espaço de confiança, com sigilo e acolhimento, que também dialoga com a família de forma cuidadosa e ética.",
        ],
      },
      {
        heading: "E quando há dúvidas sobre atenção, aprendizagem ou comportamento?",
        paragraphs: [
          "Em algumas situações, as dúvidas vão além do emocional: dificuldades persistentes de atenção, organização ou aprendizagem, ou questões do neurodesenvolvimento que a família gostaria de compreender melhor. Nesses casos, a avaliação neuropsicológica pode ser considerada — sempre como um processo estruturado e individualizado.",
          "O primeiro passo, em ambos os caminhos, é uma conversa inicial para compreender a demanda e orientar a família com clareza.",
        ],
      },
    ],
  },
  {
    slug: "vida-adulta-rotina-sobrecarga-e-saude-emocional",
    title: "Vida adulta: rotina, sobrecarga e saúde emocional",
    category: "Vida Adulta",
    minutes: 4,
    date: "2026-05-15",
    excerpt: "Trabalho, relações, responsabilidades e a sensação de que o tempo nunca é suficiente. Entenda como as demandas da vida adulta impactam a saúde emocional — e quando buscar apoio.",
    sections: [
      {
        paragraphs: [
          "A vida adulta costuma reunir muitas frentes ao mesmo tempo: trabalho, finanças, relações, família, casa — e a expectativa de dar conta de tudo. Nesse ritmo, é comum que a saúde emocional fique para depois, até que sinais como esgotamento, irritabilidade, ansiedade ou sensação de desorganização constante comecem a pesar.",
          "Cuidar da saúde emocional não é luxo nem sinal de fraqueza: é parte da manutenção de uma vida com qualidade.",
        ],
      },
      {
        heading: "Quando a dificuldade é antiga",
        paragraphs: [
          "Algumas pessoas adultas carregam, desde a infância, desafios de atenção, organização e planejamento que nunca foram investigados. Perceber esses padrões na vida adulta — na rotina profissional, nos prazos, na casa, nas finanças — é frequente e legítimo.",
          "Nesses casos, além da psicoterapia, uma avaliação neuropsicológica pode contribuir para compreender o funcionamento e orientar caminhos de organização da rotina com estratégias adequadas ao perfil de cada pessoa.",
        ],
      },
      {
        heading: "O que a psicoterapia oferece",
        paragraphs: [
          "Um espaço para pausar, organizar pensamentos e emoções, compreender padrões e construir formas mais saudáveis de lidar com as demandas — sem fórmulas prontas, no ritmo e na singularidade de cada história.",
          "Se a sua rotina tem pesado mais do que deveria, uma conversa inicial já pode ser um bom começo.",
        ],
      },
    ],
  },
  {
    slug: "o-que-sao-transtornos-de-aprendizagem",
    title: "O que são transtornos de aprendizagem?",
    category: "Aprendizagem",
    minutes: 5,
    date: "2026-04-28",
    excerpt: "Dislexia, discalculia e outras condições específicas afetam a forma de aprender — não a inteligência. Entenda o que são os transtornos de aprendizagem e como são compreendidos.",
    sections: [
      {
        paragraphs: [
          "Os transtornos específicos de aprendizagem são condições do neurodesenvolvimento que afetam habilidades escolares específicas — como leitura (dislexia), escrita ou matemática (discalculia) — em pessoas com inteligência preservada e oportunidades adequadas de ensino.",
          "Esse ponto é essencial: uma criança com transtorno de aprendizagem não é 'menos inteligente'. Ela aprende de uma forma diferente — e compreender essa forma é o que permite construir estratégias efetivas.",
        ],
      },
      {
        heading: "Como essas dificuldades costumam aparecer",
        paragraphs: [
          "Leitura lenta ou com trocas frequentes, dificuldade persistente para compreender textos, erros de escrita que não acompanham o esperado para a série, ou grande desafio com conceitos matemáticos são exemplos de sinais que merecem atenção — especialmente quando persistem apesar do esforço e do acompanhamento escolar.",
          "Também é importante lembrar: nem toda dificuldade escolar é um transtorno. Atenção, aspectos emocionais, métodos de ensino e contexto familiar também influenciam a aprendizagem.",
        ],
      },
      {
        heading: "O papel da avaliação neuropsicológica",
        paragraphs: [
          "A avaliação neuropsicológica é o processo que permite compreender o perfil de aprendizagem da criança: habilidades de linguagem, raciocínio, memória, funcionamento intelectual e aspectos emocionais, sempre articulados com a história escolar e familiar.",
          "A partir dessa compreensão, família e escola recebem orientações sobre caminhos de suporte adequados — o que costuma transformar a relação da criança com a aprendizagem.",
        ],
      },
    ],
  },
  {
    slug: "quando-procurar-avaliacao-neuropsicologica",
    title: "Quando procurar uma avaliação neuropsicológica?",
    category: "Neuropsicologia",
    minutes: 5,
    date: "2026-01-15",
    excerpt: "Dúvidas sobre atenção, memória, aprendizagem ou desenvolvimento podem motivar uma avaliação. Entenda o que esse processo investiga e quando ele pode ser considerado.",
    sections: [
      {
        paragraphs: [
          "É comum que a procura por uma avaliação neuropsicológica nasça de uma inquietação: uma criança que apresenta dificuldades na escola, um adulto que percebe há anos desafios de atenção e organização, ou uma família que recebeu da escola a sugestão de investigar o desenvolvimento.",
          "A avaliação neuropsicológica é um processo clínico estruturado que busca compreender como a pessoa funciona — suas capacidades cognitivas, emocionais e comportamentais — sempre considerando história, contexto e particularidades.",
        ],
      },
      {
        heading: "Demandas que podem motivar a avaliação",
        paragraphs: [
          "Diferentes questões podem levar a uma avaliação: dúvidas ou hipóteses relacionadas a TDAH e TEA, dificuldades de aprendizagem, queixas de atenção e memória, questões de funções executivas (como planejamento e organização), funcionamento cognitivo e do desenvolvimento, além de aspectos emocionais e comportamentais, quando clinicamente pertinentes.",
          "Importante: a avaliação não é um teste único, nem uma forma de 'descobrir um diagnóstico' em uma sessão. É um processo com etapas — entrevista, planejamento, sessões, análise e devolutiva.",
        ],
      },
      {
        heading: "Por onde começar",
        paragraphs: [
          "O primeiro passo costuma ser uma entrevista inicial, na qual a demanda é compreendida em profundidade. A partir dela, o planejamento é apresentado de forma individualizada, com transparência sobre cada etapa.",
          "Se você tem uma dúvida sobre si mesmo, sobre seu filho ou sua filha, o primeiro contato já pode ajudar a compreender se a avaliação é o caminho mais adequado.",
        ],
      },
    ],
  },
  {
    slug: "psicoterapia-e-avaliacao-neuropsicologica",
    title: "Psicoterapia e avaliação neuropsicológica são a mesma coisa?",
    category: "Psicologia",
    minutes: 4,
    date: "2026-02-02",
    excerpt: "Embora possam se complementar, psicoterapia e avaliação neuropsicológica são processos diferentes, com objetivos e formatos distintos. Entenda cada um.",
    sections: [
      {
        paragraphs: [
          "Essa é uma dúvida frequente — e legítima. Os dois processos acontecem no campo da Psicologia, podem ser conduzidos pelo mesmo profissional e, em muitos casos, se complementam. Mas têm objetivos e formatos diferentes.",
        ],
      },
      {
        heading: "Psicoterapia: um processo contínuo",
        paragraphs: [
          "A psicoterapia é um acompanhamento desenvolvido a partir da escuta, da compreensão da demanda e de objetivos construídos ao longo do processo. É um espaço para compreender, elaborar e construir novos caminhos — sem promessas de resultado em determinado número de sessões, pois cada trajetória é singular.",
        ],
      },
      {
        heading: "Avaliação neuropsicológica: um processo estruturado com etapas",
        paragraphs: [
          "A avaliação neuropsicológica tem começo, meio e fim definidos: entrevista inicial, planejamento, sessões de avaliação, integração e análise das informações e devolutiva com a documentação pertinente. O objetivo é compreender o funcionamento cognitivo, emocional e comportamental da pessoa.",
          "Em algumas situações, um processo indica o outro: a avaliação pode recomendar acompanhamento psicoterápico, e a psicoterapia pode identificar a pertinência de uma avaliação.",
        ],
      },
    ],
  },
  {
    slug: "avaliacao-neuropsicologica-infantil",
    title: "Como funciona uma avaliação neuropsicológica infantil?",
    category: "Infância",
    minutes: 5,
    date: "2026-02-20",
    excerpt: "Na infância, a avaliação considera desenvolvimento, família e escola. Saiba como o processo é conduzido com crianças e qual o papel dos responsáveis.",
    sections: [
      {
        paragraphs: [
          "Quando a demanda envolve uma criança, a avaliação neuropsicológica olha para além dela: desenvolvimento, emoções, comportamento, relações familiares e contexto escolar fazem parte da compreensão.",
        ],
      },
      {
        heading: "O papel da família",
        paragraphs: [
          "A entrevista inicial costuma acontecer com os responsáveis, momento em que a história de desenvolvimento é compreendida em detalhes: gestação, marcos do desenvolvimento, trajetória escolar e as inquietações atuais.",
          "Ao longo do processo, a família participa ativamente — e, quando pertinente, a escola também pode contribuir com informações, sempre com consentimento dos responsáveis.",
        ],
      },
      {
        heading: "Como são as sessões com a criança",
        paragraphs: [
          "As sessões são planejadas conforme a idade e a demanda, com atividades e instrumentos adequados a cada fase do desenvolvimento. O ambiente é conduzido para que a criança se sinta acolhida — o que também faz parte da qualidade do processo.",
          "Ao final, a devolutiva apresenta os resultados com clareza para a família, acompanhada da documentação pertinente, conforme as normas do Conselho Federal de Psicologia.",
        ],
      },
    ],
  },
  {
    slug: "tdah-em-adultos",
    title: "TDAH em adultos: quando uma avaliação pode ser considerada?",
    category: "TDAH",
    minutes: 5,
    date: "2026-03-10",
    excerpt: "Muitos adultos convivem há anos com desafios de atenção, organização e rotina sem nunca terem investigado. Entenda quando uma avaliação pode fazer sentido.",
    sections: [
      {
        paragraphs: [
          "Não é raro que adultos cheguem à clínica com uma história longa de desafios: dificuldade de sustentar a atenção, procrastinação frequente, sensação de desorganização constante, esquecimentos que atrapalham a rotina e a vida profissional.",
          "Em parte desses casos, surge a dúvida: seria TDAH? A resposta ética e técnica é: só um processo de avaliação estruturado pode contribuir para essa compreensão.",
        ],
      },
      {
        heading: "Por que não basta um teste rápido",
        paragraphs: [
          "Questionários rápidos encontrados na internet não avaliam o funcionamento de uma pessoa. Até porque ansiedade, sono, sobrecarga e outros fatores podem produzir queixas parecidas — e diferenciar essas possibilidades é justamente parte do trabalho da avaliação neuropsicológica.",
          "A avaliação investiga atenção, memória, funções executivas e outros aspectos, sempre articulando os resultados com a história de vida. É essa análise integrada que sustenta conclusões responsáveis.",
        ],
      },
      {
        heading: "O primeiro passo",
        paragraphs: [
          "Se essas inquietações fazem sentido para você, o caminho começa com uma conversa inicial. Nela, é possível compreender a demanda e avaliar se a avaliação neuropsicológica é o processo mais adequado para o seu momento.",
        ],
      },
    ],
  },
  {
    slug: "dificuldade-de-atencao-por-onde-comecar",
    title: "Meu filho apresenta dificuldade de atenção. Por onde começar?",
    category: "Infância",
    minutes: 4,
    date: "2026-04-05",
    excerpt: "Quando a escola ou a família percebe dificuldades de atenção na criança, surgem muitas dúvidas. Veja como organizar o primeiro passo com calma e informação.",
    sections: [
      {
        paragraphs: [
          "A percepção costuma chegar por caminhos diferentes: um retorno da escola, a dificuldade nas lições de casa, a inquietação que parece maior que a das outras crianças. Diante disso, é natural que a família se pergunte o que fazer — e por onde começar.",
        ],
      },
      {
        heading: "Atenção é uma função ampla",
        paragraphs: [
          "Dificuldades de atenção podem ter origens diversas: questões do desenvolvimento, aspectos emocionais, rotina, sono, contexto escolar e, em alguns casos, condições como o TDAH. Por isso, conclusões apressadas raramente ajudam — compreender a criança de forma ampla, sim.",
          "O acompanhamento psicológico infantil e a avaliação neuropsicológica são caminhos que podem se articular. Em uma conversa inicial, é possível compreender a demanda e orientar sobre o percurso mais adequado.",
        ],
      },
      {
        heading: "O que a família pode fazer agora",
        paragraphs: [
          "Observe e registre: em quais momentos a dificuldade aparece? Desde quando? Em quais contextos? Essas informações são valiosas na entrevista inicial e ajudam a construir um planejamento criterioso — seja para acompanhamento psicoterápico, seja para avaliação.",
        ],
      },
    ],
  },
  {
    slug: "dificuldade-escolar-e-transtorno-de-aprendizagem",
    title: "Dificuldade escolar significa transtorno de aprendizagem?",
    category: "Aprendizagem",
    minutes: 4,
    date: "2026-05-12",
    excerpt: "Nem toda dificuldade escolar indica um transtorno de aprendizagem. Entenda a diferença e como a avaliação neuropsicológica pode contribuir para essa compreensão.",
    sections: [
      {
        paragraphs: [
          "Notas baixas, resistência às tarefas, leitura mais lenta que a dos colegas: dificuldades escolares preocupam famílias e merecem atenção. Mas é importante saber: dificuldade escolar não significa, automaticamente, transtorno de aprendizagem.",
        ],
      },
      {
        heading: "Muitos fatores podem estar em jogo",
        paragraphs: [
          "A aprendizagem é influenciada por aspectos cognitivos, emocionais, pedagógicos e contextuais. Uma criança pode apresentar dificuldades por questões de atenção, por fatores emocionais como a ansiedade, por métodos de ensino que não dialogam com sua forma de aprender — ou, em alguns casos, por um transtorno específico de aprendizagem.",
          "Distinguir essas possibilidades é exatamente o papel de um processo de avaliação bem conduzido, que analisa linguagem, raciocínio, memória, funcionamento intelectual e aspectos emocionais em conjunto com a história escolar.",
        ],
      },
      {
        heading: "Compreender antes de concluir",
        paragraphs: [
          "Mais importante do que encontrar um rótulo é compreender o que está acontecendo com a criança — suas dificuldades e também suas potencialidades. É a partir dessa compreensão que caminhos efetivos podem ser construídos com família e escola.",
        ],
      },
    ],
  },
];

export const findArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
